import { useState } from "react";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Zap, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { errorMessage } from "@/lib/errors";

// Stripe hosts our billing portal on billing.stripe.com; we still allow
// checkout.stripe.com for parity with the checkout redirect flow.
const ALLOWED_STRIPE_HOSTS = new Set([
  "billing.stripe.com",
  "checkout.stripe.com",
]);

function assertStripePortalUrl(raw) {
  if (typeof raw !== "string" || !raw) return null;
  let u;
  try { u = new URL(raw); } catch { return null; }
  if (u.protocol !== "https:") return null;
  if (!ALLOWED_STRIPE_HOSTS.has(u.hostname)) return null;
  return u;
}

const TIER_LABEL = {
  free: "Free",
  pro: "Pro",
  premium: "Premium",
};

/**
 * Manage Subscription panel — surfaces the user's current tier and hands
 * them off to the Stripe Billing Portal to change payment method, download
 * invoices, upgrade / downgrade, or cancel.
 *
 * Free users see a promo card that links to /app/pricing.
 * Legacy-priced users see a note explaining why upgrading via the portal
 * may result in a different (current) price for their new plan.
 */
export default function ManageSubscription() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const tier = String(user?.subscription_tier || "free").toLowerCase();
  const period = user?.subscription_period;
  const currency = user?.subscription_currency || "USD";
  const isPaid = tier === "pro" || tier === "premium";

  const openPortal = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/app/settings#billing`,
      });
      const parsed = assertStripePortalUrl(data?.url);
      if (!parsed) {
        console.error("[portal] invalid portal URL", data);
        toast.error("We couldn't open the billing portal — please try again in a moment.");
        setBusy(false);
        return;
      }
      window.location.assign(parsed.toString());
      // Do not clear busy: the page is unloading; leaving the loading label
      // prevents duplicate portal creations during the brief navigation gap.
    } catch (e) {
      toast.error(errorMessage(e, "Couldn't open the billing portal."));
      setBusy(false);
    }
  };

  const goPricing = () => {
    window.location.assign("/app/pricing");
  };

  return (
    <div id="billing" data-testid="manage-subscription" className="space-y-6">
      <div className="card-flat p-6" data-testid="current-plan-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Current plan
            </div>
            <div className="mt-2 flex items-center gap-2">
              {tier === "premium" && <Sparkles size={18} className="text-purple-500" />}
              {tier === "pro" && <Zap size={18} className="text-primary" />}
              <div
                className="font-display font-black text-3xl tracking-tight"
                data-testid="current-plan-name"
              >
                {TIER_LABEL[tier] || TIER_LABEL.free}
              </div>
            </div>
            {isPaid && (
              <div className="mt-2 text-sm text-muted-foreground" data-testid="current-plan-cycle">
                Billed {period || "monthly"} · charged in {currency}
              </div>
            )}
            {!isPaid && (
              <div className="mt-2 text-sm text-muted-foreground">
                You&apos;re on the free plan. Upgrade to unlock unlimited AI coaching,
                advanced roadmaps, and voice interviews.
              </div>
            )}
          </div>

          {isPaid ? (
            <Button
              data-testid="open-billing-portal"
              onClick={openPortal}
              disabled={busy}
              className="inline-flex items-center gap-2"
            >
              <CreditCard size={16} />
              {busy ? "Opening portal…" : "Manage plan"}
              <ExternalLink size={14} className="opacity-70" />
            </Button>
          ) : (
            <Button
              data-testid="see-plans"
              onClick={goPricing}
              className="inline-flex items-center gap-2"
            >
              See plans
            </Button>
          )}
        </div>
      </div>

      {isPaid && (
        <div
          data-testid="portal-help-card"
          className="card-flat p-5 text-sm text-muted-foreground space-y-2"
        >
          <div className="font-medium text-foreground">What you can do in the billing portal</div>
          <ul className="space-y-1 list-disc pl-5">
            <li>Change payment method or update card details</li>
            <li>Download invoices &amp; receipts</li>
            <li>Upgrade or downgrade your plan (proration is automatic)</li>
            <li>Cancel or pause your subscription</li>
          </ul>
          {user?.legacy_pricing_id && (
            <div
              data-testid="legacy-price-note"
              className="mt-3 rounded-md border border-border/60 bg-muted/40 p-3 text-xs"
            >
              Heads up — you&apos;re on a grandfathered legacy price. If you change
              tiers in the portal, your new plan will use current standard
              pricing.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
